import React, { FC, useCallback, useMemo, useState } from 'react';
import EachWOTask from './EachWOTask';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { Button, Popconfirm, Typography } from 'antd';
import InlineEdit from './InlineEdit';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;

const EachTaskGroup: FC<any> = ({
	group,
	onTaskEdit,
	onTaskDelete,
	onGroupDelete,
	addNewTask,
	isAllowedToEdit,
	onEditGroupTitleComplete,
	timezonId,
	notCheckable,
	onShowAssociate,
	workOrder,
}): React.ReactElement => {
	const [editTitle, setEditTitle] = useState(false);

	const onAddNewTask = useCallback(() => {
		addNewTask(nullSafeGetOrElse('groupTitle', group, ''))();
	}, [addNewTask, group]);

	const onFinishGroupTitle = useCallback(
		(newTitle) => {
			if (!(newTitle || '').trim()) return;
			setEditTitle(false);
			onEditGroupTitleComplete(group.groupTitle, newTitle);
		},
		[group.groupTitle, onEditGroupTitleComplete]
	);

	const onGroupTitleClick = () => setEditTitle(true);

	const taskWithIdAvailable = useMemo(
		() => nullSafeGetOrElse('tasks', group, []).some((_) => !!_.id),
		[group]
	);

	const onDeleteGroup = useCallback(() => {
		onGroupDelete(group.groupTitle);
	}, [group, onGroupDelete]);

	return (
		<div className="mb-4 flex flex-col">
			<div className="group flex flex-row items-center">
				{isAllowedToEdit && editTitle ? (
					<InlineEdit
						className="bold text-xl"
						placeholder="Enter group title"
						value={group.groupTitle}
						setValue={onFinishGroupTitle}
					/>
				) : (
					<Text
						onClick={onGroupTitleClick}
						className={`mb-1 text-xl font-medium ${isAllowedToEdit ? 'cursor-pointer' : ''}`}
					>
						{group.groupTitle}
					</Text>
				)}
				<div
					className={`ml-4 flex hidden flex-row items-center ${
						isAllowedToEdit ? 'hover:cursor-pointer group-hover:flex' : ''
					}`}
				>
					{!editTitle ? (
						<div className="mr-4">
							<EditOutlined onClick={onGroupTitleClick} />
						</div>
					) : null}
					{taskWithIdAvailable ? (
						<Popconfirm
							onConfirm={onDeleteGroup}
							placement="topLeft"
							title="Are you sure you want to delete?"
							okText="Yes"
							cancelText="No"
						>
							<DeleteOutlined />
						</Popconfirm>
					) : (
						<DeleteOutlined onClick={onDeleteGroup} />
					)}
				</div>
			</div>
			<div className="flex flex-col justify-start">
				{group.tasks.map((task) => (
					<EachWOTask
						key={`task-item-${group.groupTitle}-${task.id || task.orderingId}`}
						task={task}
						onTaskEdit={onTaskEdit}
						onTaskDelete={onTaskDelete}
						onEnter={onAddNewTask}
						canEdit={isAllowedToEdit}
						timezonId={timezonId}
						notCheckable={notCheckable}
						onShowAssociate={onShowAssociate}
						workOrder={workOrder}
					/>
				))}

				{isAllowedToEdit ? (
					<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_WO_TASKS}>
						<div className="padding-less-antd-button my-2 flex flex-row pl-1">
							<Button onClick={onAddNewTask} type="link">
								<PlusOutlined /> Add task
							</Button>
						</div>
					</AccessPermissionChecker>
				) : null}
			</div>
		</div>
	);
};

export default EachTaskGroup;
